/**
 * useRecipeTemplates Hook
 *
 * Simple hook for fetching recipe templates for dropdown selection.
 * Provides minimal data optimised for select/combobox components.
 *
 * Part of US-P2-010: Process Recipe Management
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch active recipe templates for dropdown selection
 * Returns minimal data optimised for select components
 *
 * @param {string} recipeType - Optional filter by recipe type ('block', 'spawn', 'liquid_culture', 'other')
 * @param {string} strainId - Optional filter by strain ID (null includes strain-agnostic recipes)
 * @returns {Object} { templates, loading, error, refetch }
 */
export function useRecipeTemplates(recipeType = null, strainId = null) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('recipe_templates')
        .select(`
          id,
          name,
          description,
          recipe_type,
          strain_id,
          strain:strains(id, name, code, origin)
        `)
        .eq('is_active', true)
        .order('name')

      if (recipeType) {
        query = query.eq('recipe_type', recipeType)
      }

      // If strainId is provided, get recipes for that strain OR generic recipes (strain_id IS NULL)
      if (strainId) {
        query = query.or(`strain_id.eq.${strainId},strain_id.is.null`)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      // Transform for easier use in dropdowns
      const transformed = (data || []).map(template => ({
        id: template.id,
        name: template.name,
        description: template.description,
        recipe_type: template.recipe_type,
        strain_id: template.strain_id,
        strain_name: template.strain?.name || null,
        strain_code: template.strain?.code || null,
        // Display label for dropdown
        label: template.strain
          ? `${template.name} (${template.strain.code})`
          : template.name,
        // Indicates if this is a generic (strain-agnostic) recipe
        is_generic: !template.strain_id
      }))

      setTemplates(transformed)
    } catch (err) {
      console.error('[useRecipeTemplates] Error:', err)
      setError(err.message)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [recipeType, strainId])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates
  }
}

/**
 * Hook to fetch recipe templates grouped by type
 * Useful for organised selection interfaces
 *
 * @param {string} strainId - Optional filter by strain ID
 * @returns {Object} { templatesByType, allTemplates, loading, error, refetch }
 */
export function useRecipeTemplatesByType(strainId = null) {
  const [templatesByType, setTemplatesByType] = useState({
    block: [],
    spawn: [],
    liquid_culture: [],
    other: []
  })
  const [allTemplates, setAllTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('recipe_templates')
        .select(`
          id,
          name,
          description,
          recipe_type,
          strain_id,
          strain:strains(id, name, code, origin)
        `)
        .eq('is_active', true)
        .order('recipe_type')
        .order('name')

      // If strainId is provided, get recipes for that strain OR generic recipes
      if (strainId) {
        query = query.or(`strain_id.eq.${strainId},strain_id.is.null`)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      // Transform and group by type
      const grouped = {
        block: [],
        spawn: [],
        liquid_culture: [],
        other: []
      }

      const transformed = (data || []).map(template => {
        const item = {
          id: template.id,
          name: template.name,
          description: template.description,
          recipe_type: template.recipe_type,
          strain_id: template.strain_id,
          strain_name: template.strain?.name || null,
          strain_code: template.strain?.code || null,
          label: template.strain
            ? `${template.name} (${template.strain.code})`
            : template.name,
          is_generic: !template.strain_id
        }

        // Add to appropriate group
        if (grouped[template.recipe_type]) {
          grouped[template.recipe_type].push(item)
        } else {
          grouped.other.push(item)
        }

        return item
      })

      setTemplatesByType(grouped)
      setAllTemplates(transformed)
    } catch (err) {
      console.error('[useRecipeTemplatesByType] Error:', err)
      setError(err.message)
      setTemplatesByType({
        block: [],
        spawn: [],
        liquid_culture: [],
        other: []
      })
      setAllTemplates([])
    } finally {
      setLoading(false)
    }
  }, [strainId])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  return {
    templatesByType,
    allTemplates,
    // Convenience accessors
    blockRecipes: templatesByType.block,
    spawnRecipes: templatesByType.spawn,
    lcRecipes: templatesByType.liquid_culture,
    otherRecipes: templatesByType.other,
    // State
    loading,
    error,
    refetch: fetchTemplates
  }
}

/**
 * Recipe type labels for display
 */
export const RECIPE_TYPE_LABELS = {
  block: 'Block/Substrate',
  spawn: 'Spawn',
  liquid_culture: 'Liquid Culture',
  other: 'Other'
}

/**
 * Recipe type options for select components
 */
export const RECIPE_TYPE_OPTIONS = [
  { value: 'block', label: 'Block/Substrate' },
  { value: 'spawn', label: 'Spawn' },
  { value: 'liquid_culture', label: 'Liquid Culture' },
  { value: 'other', label: 'Other' }
]

/**
 * Per-unit type labels for display
 */
export const PER_UNIT_LABELS = {
  per_block: 'Per Block',
  per_bag: 'Per Bag',
  per_jar: 'Per Jar',
  per_batch: 'Per Batch'
}

/**
 * Per-unit type options for select components
 */
export const PER_UNIT_OPTIONS = [
  { value: 'per_block', label: 'Per Block' },
  { value: 'per_bag', label: 'Per Bag' },
  { value: 'per_jar', label: 'Per Jar' },
  { value: 'per_batch', label: 'Per Batch' }
]

export default useRecipeTemplates
